/**
 * MarkdownRenderer Component
 *
 * Renders markdown content with proper styling for discussions and chat messages.
 * Includes XSS sanitization via rehype-sanitize for security.
 *
 * Features:
 * - Syntax-highlighted code blocks with language label
 * - Styled lists (ordered and unordered)
 * - Links that open in new tab
 * - Blockquotes with border styling
 * - Tables with proper borders
 * - GFM (GitHub Flavored Markdown) support
 * - XSS protection via rehype-sanitize
 *
 * @module components/shared/MarkdownRenderer
 * @see AC-4.2.3.5 - Content containing script tags is sanitized
 */

import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeSanitize from 'rehype-sanitize';
import type { Components } from 'react-markdown';

/**
 * Props for the MarkdownRenderer component
 */
export interface MarkdownRendererProps {
  /** Markdown content to render */
  content: string;
  /** Additional className for the container */
  className?: string;
}

/**
 * Custom components for ReactMarkdown
 */
const markdownComponents: Components = {
  // Code elements - inline code only (block code is handled by pre)
  code: ({ className, children, ...props }) => {
    // Check if this is inside a pre tag (block code) by checking className
    const match = /language-(\w+)/.exec(className || '');

    // If it has a language class, it's inside a pre block - just render code
    if (match) {
      return (
        <code className={`font-mono text-sm ${className || ''}`} {...props}>
          {children}
        </code>
      );
    }

    // Inline code
    return (
      <code
        className="px-1.5 py-0.5 rounded bg-muted font-mono text-sm"
        data-testid="inline-code"
        {...props}
      >
        {children}
      </code>
    );
  },

  // Pre elements - block code containers
  pre: ({ children, ...props }) => {
    // Extract language from the code child if present
    const codeChild = children as React.ReactElement<{ className?: string }>;
    const className = codeChild?.props?.className || '';
    const match = /language-(\w+)/.exec(className);
    const language = match ? match[1] : '';

    return (
      <div className="relative my-2" data-testid="code-block">
        {language && (
          <div className="absolute top-0 right-0 px-2 py-1 text-xs text-muted-foreground bg-muted rounded-bl">
            {language}
          </div>
        )}
        <pre className="p-4 rounded-md bg-muted overflow-x-auto" {...props}>
          {children}
        </pre>
      </div>
    );
  },

  // Styled paragraphs
  p: ({ children }) => (
    <p className="mb-2 last:mb-0 leading-relaxed">{children}</p>
  ),

  // Unordered lists
  ul: ({ children }) => (
    <ul className="list-disc list-inside mb-2 pl-2 space-y-1" data-testid="markdown-ul">
      {children}
    </ul>
  ),

  // Ordered lists
  ol: ({ children }) => (
    <ol className="list-decimal list-inside mb-2 pl-2 space-y-1" data-testid="markdown-ol">
      {children}
    </ol>
  ),

  // List items
  li: ({ children }) => (
    <li className="leading-relaxed">{children}</li>
  ),

  // Links
  a: ({ href, children }) => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="text-primary underline hover:no-underline"
      data-testid="markdown-link"
    >
      {children}
    </a>
  ),

  // Blockquotes
  blockquote: ({ children }) => (
    <blockquote
      className="border-l-4 border-muted-foreground/30 pl-4 my-2 italic text-muted-foreground"
      data-testid="markdown-blockquote"
    >
      {children}
    </blockquote>
  ),

  // Headers
  h1: ({ children }) => (
    <h1 className="text-xl font-bold mt-4 mb-2" data-testid="markdown-h1">{children}</h1>
  ),
  h2: ({ children }) => (
    <h2 className="text-lg font-bold mt-3 mb-2" data-testid="markdown-h2">{children}</h2>
  ),
  h3: ({ children }) => (
    <h3 className="text-base font-bold mt-2 mb-1" data-testid="markdown-h3">{children}</h3>
  ),
  h4: ({ children }) => (
    <h4 className="text-sm font-bold mt-2 mb-1" data-testid="markdown-h4">{children}</h4>
  ),
  h5: ({ children }) => (
    <h5 className="text-sm font-semibold mt-2 mb-1" data-testid="markdown-h5">{children}</h5>
  ),
  h6: ({ children }) => (
    <h6 className="text-sm font-semibold mt-2 mb-1 text-muted-foreground" data-testid="markdown-h6">{children}</h6>
  ),

  // Horizontal rules
  hr: () => (
    <hr className="my-4 border-muted" />
  ),

  // Strong/bold text
  strong: ({ children }) => (
    <strong className="font-semibold">{children}</strong>
  ),

  // Emphasis/italic text
  em: ({ children }) => (
    <em className="italic">{children}</em>
  ),

  // Tables (GFM)
  table: ({ children }) => (
    <div className="overflow-x-auto my-2">
      <table className="min-w-full border-collapse border border-muted" data-testid="markdown-table">
        {children}
      </table>
    </div>
  ),
  thead: ({ children }) => (
    <thead className="bg-muted">{children}</thead>
  ),
  th: ({ children }) => (
    <th className="border border-muted px-3 py-2 text-left font-semibold">{children}</th>
  ),
  td: ({ children }) => (
    <td className="border border-muted px-3 py-2">{children}</td>
  ),
};

/**
 * MarkdownRenderer component - renders markdown with chat-friendly styling
 *
 * Includes XSS sanitization via rehype-sanitize default schema to strip dangerous elements:
 * - `<script>` tags
 * - `onclick` and other event handler attributes
 * - `javascript:` URLs
 * - `<iframe>`, `<object>`, `<embed>` tags
 * - `data:` URLs (except for images)
 *
 * Security Note: The default rehype-sanitize schema provides strong protection against
 * common XSS vectors. For governance content, this is sufficient as all user-generated
 * markdown is sanitized before rendering. A custom stricter schema could be implemented
 * if additional restrictions are needed in the future.
 *
 * @see https://github.com/rehypejs/rehype-sanitize#use
 *
 * @example
 * ```tsx
 * <MarkdownRenderer content="# Hello\n\nThis is **bold** text." />
 * ```
 */
export function MarkdownRenderer({ content, className = '' }: MarkdownRendererProps) {
  return (
    <div
      className={`prose prose-sm max-w-none dark:prose-invert ${className}`}
      data-testid="markdown-renderer"
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeSanitize]}
        components={markdownComponents}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
}

export default MarkdownRenderer;
